import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { Fragment, Suspense, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl-next';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/utility/migration/chevron-right';
import { SmartCardProvider as Provider } from '@atlaskit/link-provider';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onExpandButtonClicked } from '../../../ai-answer-dialog/analytics';
import { ErrorBoundary } from '../error-boundary';
import { messages } from './messages';
import { SourceCard } from './source-card';
var cardTitleStyles = xcss({
  marginTop: 'space.100',
  color: 'color.text.subtlest',
  font: 'font.heading.xxsmall'
});
var containerStyles = xcss({
  width: '100%',
  marginTop: 'space.075'
});
var freeflowLinks = xcss({
  display: 'inline',
  marginRight: 'space.075',
  lineHeight: 2
});
var iconWrapperStyles = xcss({
  width: '24px',
  height: '24px',
  cursor: 'pointer'
});
var Sources = function Sources(_ref) {
  var sources = _ref.sources,
    searchSessionId = _ref.searchSessionId,
    onClickHandler = _ref.onClickHandler,
    allowFreeflowLayout = _ref.allowFreeflowLayout,
    verticalLayout = _ref.verticalLayout,
    _ref$enableSmartLinkS = _ref.enableSmartLinkSources,
    enableSmartLinkSources = _ref$enableSmartLinkS === void 0 ? true : _ref$enableSmartLinkS;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    showAllSources = _useState2[0],
    setShowAllSources = _useState2[1];
  var _useAIAnswerContext = useAIAnswerContext(),
    fireAnalyticsEvent = _useAIAnswerContext.fireAnalyticsEvent,
    commonAttributes = _useAIAnswerContext.commonAttributes;
  var canExpand = sources.length > 1 && verticalLayout;
  var visibleSources = showAllSources || !verticalLayout ? sources : sources.slice(0, 1);
  var onExpandButtonClick = useCallback(function () {
    fireAnalyticsEvent(onExpandButtonClicked(_objectSpread(_objectSpread({}, commonAttributes), {}, {
      expandButtonSource: 'sources'
    })));
    setShowAllSources(!showAllSources);
  }, [fireAnalyticsEvent, commonAttributes, showAllSources]);
  return /*#__PURE__*/React.createElement(Fragment, null, visibleSources.map(function (source, index) {
    return /*#__PURE__*/React.createElement(Inline, {
      key: "ai-answer-source-".concat(index),
      xcss: allowFreeflowLayout ? freeflowLinks : null,
      space: "space.050",
      alignBlock: "center"
    }, /*#__PURE__*/React.createElement(SourceCard, {
      index: index,
      key: "search-ai-answer-source-".concat(index),
      source: source,
      searchSessionId: searchSessionId,
      onClickHandler: onClickHandler,
      enableSmartLinkSources: enableSmartLinkSources
    }), index === 0 && canExpand && /*#__PURE__*/React.createElement(Box, {
      xcss: iconWrapperStyles,
      onClick: onExpandButtonClick
    }, showAllSources ? /*#__PURE__*/React.createElement(ChevronDownIcon, {
      color: "currentColor",
      label: "",
      testId: "collapse-sources-icon"
    }) : /*#__PURE__*/React.createElement(ChevronRightIcon, {
      color: "currentColor",
      label: "",
      testId: "expand-sources-icon"
    })));
  }));
};
var LayoutWrapper = function LayoutWrapper(props) {
  if (props.allowFreeflowLayout) {
    return /*#__PURE__*/React.createElement("div", null, props.children);
  } else if (props.verticalLayout) {
    return /*#__PURE__*/React.createElement(Stack, {
      space: "space.075"
    }, props.children);
  } else {
    return /*#__PURE__*/React.createElement(Inline, {
      space: "space.075",
      shouldWrap: true
    }, props.children);
  }
};
export var AISources = function AISources(props) {
  var ogSources = props.sources,
    searchSessionId = props.searchSessionId,
    title = props.title,
    _props$onClickHandler = props.onClickHandler,
    onClickHandler = _props$onClickHandler === void 0 ? function () {} : _props$onClickHandler,
    _props$allowFreeflowL = props.allowFreeflowLayout,
    allowFreeflowLayout = _props$allowFreeflowL === void 0 ? false : _props$allowFreeflowL,
    _props$verticalLayout = props.verticalLayout,
    verticalLayout = _props$verticalLayout === void 0 ? false : _props$verticalLayout,
    limit = props.limit,
    enableSmartLinkSources = props.enableSmartLinkSources;
  var sourcesTitle = title !== null && title !== void 0 ? title : messages.sources_title;
  var sources = limit ? ogSources.slice(0, limit) : ogSources;
  var MaybeSmartCardProvider = enableSmartLinkSources !== false ? Provider : Fragment;
  if (!sources.length) {
    return /*#__PURE__*/React.createElement(React.Fragment, null);
  }
  return /*#__PURE__*/React.createElement(ErrorBoundary, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(Suspense, {
    fallback: /*#__PURE__*/React.createElement(React.Fragment, null)
  }, /*#__PURE__*/React.createElement(Box, {
    xcss: cardTitleStyles
  }, /*#__PURE__*/React.createElement(FormattedMessage, _extends({}, sourcesTitle, {
    values: {
      numSources: sources.length
    }
  }))), /*#__PURE__*/React.createElement(Box, {
    xcss: containerStyles
  }, /*#__PURE__*/React.createElement(LayoutWrapper, {
    allowFreeflowLayout: allowFreeflowLayout,
    verticalLayout: verticalLayout
  }, /*#__PURE__*/React.createElement(MaybeSmartCardProvider, null, /*#__PURE__*/React.createElement(Sources, {
    sources: sources,
    searchSessionId: searchSessionId,
    onClickHandler: onClickHandler,
    allowFreeflowLayout: allowFreeflowLayout,
    verticalLayout: verticalLayout,
    enableSmartLinkSources: enableSmartLinkSources
  }))))));
};